<template>
  <div class="galileo-inertial-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$x' = x - vt | F_inertial = -ma$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 3D/2.5D 复合渲染台 -->
      <div class="simulation-stage dark-stage">
        <!-- 地面参考系 S (固定网格) -->
        <div class="grid-layer ground-grid"></div>
        
        <!-- 运动参考系 S' (惯性系或非惯性系) -->
        <div class="frame-s-prime" :style="frameStyle">
           <div class="coordinate-axes">
              <div class="axis x">X'</div>
              <div class="axis y">Y'</div>
           </div>
           
           <!-- 车厢内部：探究惯性力 -->
           <div class="cabin-interior">
              <!-- 悬挂小球 (用于检测惯性) -->
              <div class="pendulum" :style="pendulumStyle">
                 <div class="string"></div>
                 <div class="ball"></div>
                 <!-- 惯性力矢量箭头 -->
                 <div class="f-inertial-arrow" v-if="accel !== 0">
                    <div class="arrow-head"></div>
                    <span>{{ accel > 0 ? '惯性力 F=-ma' : '' }}</span>
                 </div>
              </div>
              <div class="cabin-label">参考系 S' (车厢)</div>
           </div>
        </div>

        <div class="hud-dashboard">
           <div class="hud-group">
              <label>参考系性质</label>
              <span class="val" :class="isNonInertial ? 'warn' : 'info'">{{ isNonInertial ? '非惯性系 (加速中)' : '惯性参考系 (匀速/静止)' }}</span>
           </div>
           <div class="hud-group">
              <label>框架加速度 a</label>
              <span class="val">{{ accel.toFixed(1) }} <small>m/s²</small></span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
         <div class="control-card">
            <div class="flex-row">
               <div class="control-item">
                  <label class="s-label">调节 S' 加速度 (a)</label>
                  <el-slider v-model="accel" :min="-5" :max="5" :step="0.1" size="small" @input="isNonInertial = (accel !== 0)"></el-slider>
               </div>
               <div class="control-item shrink">
                  <el-button type="primary" size="small" @click="reset">重置为惯性系</el-button>
               </div>
            </div>
         </div>

         <div class="discovery-card-compact" v-latex>
            <i class="el-icon-collection"></i>
            <p v-if="!isNonInertial"><strong>伽利略变换：</strong>在匀速直线运动的惯性系 S' 中，物理定律形式不变。小球垂直悬挂，符合牛顿定律。</p>
            <p v-else><strong>非惯性系：</strong>当参考系 a≠0 时，牛顿定律不再直接适用。为了留在参考系内分析，我们必须引入一个**“惯性力”**来解释小球的倾斜。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GalileoInertialViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
       accel: 0,
       vel: 0,
       posX: 100,
       isNonInertial: false,
       running: true
    }
  },
  computed: {
    displayTitle() {
      return this.knowledgePoint || '伽利略变换与惯性力探究';
    },
    frameStyle() {
      return { transform: `translateX(${this.posX}px)` };
    },
    pendulumStyle() {
      // 倾角 theta = arctan(a/g)
      // g approx 9.8
      const angle = Math.atan(this.accel / 9.8) * (180 / Math.PI);
      return { transform: `rotate(${-angle}deg)` };
    }
  },
  methods: {
    animate() {
      if (!this.running) return;
      // v = v0 + at
      this.vel += this.accel * 0.016;
      this.posX += this.vel * 2;
      
      // Infinite scroll effect for pos
      if (this.posX > 800) this.posX = -200;
      if (this.posX < -200) this.posX = 800;
      
      requestAnimationFrame(this.animate);
    },
    reset() {
      this.accel = 0;
      this.vel = 1; // Give it a small constant velocity to show inertia
      this.isNonInertial = false;
    }
  },
  mounted() {
    this.animate();
  },
  beforeDestroy() {
    this.running = false;
  }
}
</script>

<style scoped lang="scss">
.galileo-inertial-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 0.85rem; color: #1e293b; font-weight: 500; } .formula-chip { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; padding: 4px 12px; border-radius: 20px; font-weight: 700; font-family: monospace; font-size: 0.75rem; } }
.simulation-stage { flex: 1; min-height: 260px; background: #020617; border-radius: 12px; position: relative; overflow: hidden; border: 1.5px solid #1e293b; }
.ground-grid { position: absolute; inset: 0; background-image: radial-gradient(#1e293b 1px, transparent 1px); background-size: 40px 40px; }
.frame-s-prime { position: absolute; top: 50px; width: 180px; height: 150px; z-index: 5;
  .coordinate-axes { position: absolute; top: -20px; left: -20px; width: 100px; height: 100px; border-left: 2px solid #3b82f6; border-bottom: 2px solid #3b82f6; 
     .axis { position: absolute; font-size: 10px; color: #3b82f6; font-weight: bold; }
     .x { bottom: -15px; right: -15px; } .y { top: -15px; left: -15px; }
  }
}
.cabin-interior { width: 100%; height: 100%; background: rgba(30, 41, 59, 0.4); border: 2px solid #3b82f6; border-radius: 8px; position: relative; backdrop-filter: blur(2px);
  .cabin-label { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #94a3b8; font-size: 10px; }
}
.pendulum { position: absolute; top: 0; left: 50%; width: 2px; height: 80px; transform-origin: top center; transition: transform 0.2s ease-out;
  .string { width: 100%; height: 100%; background: #94a3b8; }
  .ball { position: absolute; bottom: -8px; left: -8px; width: 16px; height: 16px; background: #facc15; border-radius: 50%; box-shadow: 0 0 15px rgba(250, 204, 21, 0.5); }
}
.f-inertial-arrow { position: absolute; top: 100%; left: 50%; width: 40px; height: 2px; background: #ef4444; transform: translateY(10px);
  .arrow-head { position: absolute; right: 0; top: -4px; width: 0; height: 0; border-top: 5px solid transparent; border-bottom: 5px solid transparent; border-left: 10px solid #ef4444; }
  span { position: absolute; top: 8px; right: 0; color: #ef4444; font-size: 9px; white-space: nowrap; font-weight: bold; }
}
.hud-dashboard { position: absolute; top: 12px; right: 12px; display: flex; flex-direction: column; gap: 8px; 
  .hud-group { background: rgba(15, 23, 42, 0.85); border: 1px solid #1e293b; padding: 4px 12px; border-radius: 8px; text-align: right; 
     label { display: block; font-size: 10px; color: #64748b; } .val { font-size: 12px; color: #fff; font-weight: 800; 
       &.warn { color: #f97316; } &.info { color: #3b82f6; }
     }
  }
}
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #fff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; 
  .flex-row { display: flex; gap: 20px; align-items: center; .control-item { flex: 1; &.shrink { flex: 0; } .s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 4px; display: block; } } }
}
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { color: #3b82f6; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 11px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
